// ============================================================
// What the table is looking at, and what the title card says.
// ============================================================
import type { StageDoc } from './types';

/** How long the title card rides over the map after a cut - the impacts' beat, a little longer. */
export const TITLE_CARD_MS = 2500;

/** The untitled stage every session starts with. */
export function emptyStage(): StageDoc {
  return { sceneId: null, name: '', narration: '', curtain: false, travellers: [], previousSceneId: null, cutAt: null };
}

export interface TitleCard {
  name: string;
  narration: string;
  /** True while the curtain is down: the card stays until the GM raises it. */
  held: boolean;
}

/**
 * The card to show at `now`, or null.
 *
 * Held while the curtain is down; otherwise a beat of `TITLE_CARD_MS`
 * from `cutAt`, which every client reads from the same document so the
 * beat starts at one moment for the whole table. A clock behind the cut
 * (skew) shows nothing rather than a card that never ends.
 */
export function cardFor(stage: StageDoc | null, now: number): TitleCard | null {
  if (!stage?.sceneId) return null;
  if (stage.curtain) return { name: stage.name, narration: stage.narration, held: true };
  if (stage.cutAt === null) return null;
  const age = now - stage.cutAt;
  if (age < 0 || age >= TITLE_CARD_MS) return null;
  return { name: stage.name, narration: stage.narration, held: false };
}

/** The curtain hides the map from players. The GM sees the map and a badge. */
export function curtainHides(stage: StageDoc | null, isGm: boolean): boolean {
  return !!stage?.curtain && !isGm;
}

function travellersClause(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return ` ${names[0]} travels.`;
  if (names.length <= 3) return ` ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} travel.`;
  return ` ${names[0]}, ${names[1]} and ${names.length - 2} others travel.`;
}

/** The one shared log line a cut writes. */
export function cutLogLine(from: string | null, to: string, travellerNames: readonly string[]): string {
  const head = from ? `🎬 Cut from "${from}" to "${to}".` : `🎬 Cut to "${to}".`;
  return head + travellersClause(travellerNames);
}
